<template>
  <div
    :class="{'nav-open': $sidebar.showSidebar.value, 'iphone': iphone }"
    class="wrapper"
  >
    <Sidebar
      :background-color="$config.sidebar.theme"
      :logo-mini="logoMiniImg"
    >
      <template v-slot:links>
        <section
          v-for="menu in formatMenuBlock($config.sidebar.menus)"
          :key="menu.title"
        >
          <h4
            v-if="menu.title"
            class="sidebar-title"
          >
            {{ $t(menu.title) }}
          </h4>
          <SidebarItem
            v-for="item in formatMenuItems(menu.links)"
            :key="item.title"
            :link="{
              name: $t(item.title),
              img: item.img,
              icon: item.icon,
              path: formatPath(item),
              routeName: item.name,
              active:true
            }"
          />
        </section>
        <li
          class="liveChat"
          :class="{'btn-success': $intercom.visible}"
          style="width: 100%"
          @click="toggleLivechat"
        >
          <a class="nav-link">
            <i class="fas fa-comments" />
            <p>{{ $t("sidebar.menu.help.livechat") }}
              <span
                v-show="intercomUnreadCount"
                class="livechat-mail"
              >
                {{ intercomUnreadCount }}
                <img
                  :src="envelopeIconImg"
                  alt="envelope"
                >
              </span>
            </p>
          </a>
        </li>
        <div
          v-if="sidebarContactsCard"
          class="public-manager"
        >
          <ElTooltip
            :content="sidebarContactsCard.title"
            placement="right"
          >
            <div class="title">
              <span>{{ sidebarContactsCard.title }}</span>
              <i class="fas fa-headset" />
            </div>
          </ElTooltip>
          <div
            v-if="sidebarContactsCard.label"
            class="label"
          >
            {{ sidebarContactsCard.label }}
          </div>
          <div
            v-for="(item, index) in sidebarContactsCard.contacts"
            :key="index"
            class="contact"
          >
            <UiIcon
              v-if="iconMap[item.type] && (iconMap[item.type].icon || iconMap[item.type].iconSubstyle)"
              :name="item.type"
              :color="iconMap[item.type].color || '#fff'"
              :class="iconMap[item.type].icon"
              :substyle="iconMap[item.type].iconSubstyle"
            />
            <img
              v-if="iconMap[item.type] && iconMap[item.type].img"
              :src="iconMap[item.type].img"
              alt=""
            >
            <ElTooltip
              :content="item.value"
              placement="right"
            >
              <a
                :href="getSocialLink(item.type, item.value)"
                :target="isExternalContactLink(item.type) ? '_blank' : undefined"
              >
                {{ item.value }}
              </a>
            </ElTooltip>
          </div>
          <div
            v-if="sidebarContactsCard.note"
            class="note"
          >
            {{ sidebarContactsCard.note }}
          </div>
        </div>
      </template>
    </Sidebar>
    <div
      class="main-panel"
      :style="mainPanelStyles"
    >
      <div
        v-if="showTrafficBlockAlert"
        ref="trafficBlockAlert"
        class="traffic-block-alert"
      >
        <i class="fas fa-exclamation-circle" />
        <span class="ui-m-md-l">{{ trafficBlockAlertText }}</span>
      </div>
      <TopNavbar />
      <div
        :class="{content: !$route.meta.hideContent}"
        @click="toggleSidebar"
      >
        <!-- vue3-migration router-view -->
        <RouterView v-slot="{ Component }">
          <KeepAlive>
            <component :is="Component" />
          </KeepAlive>
        </RouterView>
      </div>
      <Testbox v-if="$config.environment === 'test'" />
    </div>
  </div>
  <KycModal />
</template>

<script>
import PerfectScrollbar from 'perfect-scrollbar';
import 'perfect-scrollbar/css/perfect-scrollbar.css';
import { SlideYDownTransition, ZoomCenterTransition } from '@noction/vue-bezier';
import lzString from 'lz-string';
import { useIntercom } from '@homebaseai/vue3-intercom';
import Tooltip from '@/components/Tooltip.vue';
import Testbox from '@/test/testbox';

import logoMiniImg from '@/assets/theme/icon/logo-mini.svg';
import envelopeIconImg from '@/assets/theme/sidebar/envelope-icon.svg';
import telegramImg from '@/assets/theme/icon/telegram.svg';
import msTeamsImg from '@/assets/theme/icon/ms-teams.svg';
import KycModal from '@/components/KycModal.vue';
import TopNavbar from './TopNavbar.vue';

function hasElement(className) {
  return document.getElementsByClassName(className).length > 0;
}

function initScrollbar(className) {
  if (hasElement(className)) {
    new PerfectScrollbar(`.${className}`);
  } else {
    setTimeout(() => {
      initScrollbar(className);
    }, 100);
  }
}

const SUPPORT_CONTACT_FIELDS = ['email', 'telegram', 'ms_teams', 'contact_us_form'];

const buildSupportContacts = (supportInfo) => {
  if (!supportInfo?.enabled) {
    return [];
  }

  return SUPPORT_CONTACT_FIELDS
    .filter((type) => supportInfo[type])
    .map((type) => ({ type, value: supportInfo[type] }));
};

export default {
  components: {
    KycModal,
    Tooltip,
    TopNavbar,
    SlideYDownTransition,
    ZoomCenterTransition,
    Testbox,
  },
  setup: () => ({ intercom: useIntercom() }),
  data() {
    return {
      iphone: false,
      livechatOn: false,
      intercomReady: false,
      intercomUnreadCount: 0,
      trafficBlockAlertHeight: 0,
      trafficBlockAlertResizeObserver: null,
      iconMap: {
        email: {
          icon: 'fas fa-envelope',
        },
        skype: {
          iconSubstyle: 'fa',
          color: '#4088f7',
        },
        telegram: {
          img: telegramImg,
        },
        ms_teams: {
          img: msTeamsImg,
        },
        contact_us_form: {
          icon: 'fas fa-globe',
        },
      },
    };
  },
  computed: {
    logoMiniImg: () => logoMiniImg,
    envelopeIconImg: () => envelopeIconImg,
    telegramImg: () => telegramImg,
    mainPanelStyles() {
      return {
        '--traffic-block-alert-offset': `${this.showTrafficBlockAlert ? this.trafficBlockAlertHeight : 0}px`,
      };
    },
    showTrafficBlockAlert() {
      return !!this.$store.state.settings.traffic_block?.is_blocked;
    },
    trafficBlockAlertText() {
      return this.$store.state.settings.traffic_block?.message;
    },
    pending() {
      return this.$store.state.auth.loginProgress;
    },
    sidebarContactsCard() {
      if (!this.$store.state.settings.settingsLoaded) {
        return null;
      }

      const managerInfo = this.$store.state.settings.manager_info;

      if (managerInfo) {
        return {
          title: managerInfo.name,
          label: this.$t('sidebar.menu.yourManager'),
          contacts: managerInfo.contacts || [],
          note: managerInfo.additional_information,
        };
      }

      const supportContacts = buildSupportContacts(
        this.$store.state.misc.miscSettings?.contacts,
      );

      if (supportContacts.length) {
        return {
          title: this.$t('sidebar.menu.supportContacts'),
          contacts: supportContacts,
        };
      }

      return null;
    },
  },
  watch: {
    $route(to) {
      localStorage.setItem('lastVisitedRoute', to.path);
      document.title = `${this.$t(to.name)} - ${this.$config.pageTitle}`;
    },
    showTrafficBlockAlert() {
      this.$nextTick(() => {
        this.destroyTrafficBlockAlertObserver();
        this.updateTrafficBlockAlertHeight();
        this.initTrafficBlockAlertObserver();
      });
    },
  },
  created() {
    if (localStorage.getItem('auth-token') !== 'null') {
      // const code = localStorage.getItem('language') || 'en';
      setTimeout(() => {
        this.$store.dispatch('loginSuccess');
      }, 1000);
      // this.$store.dispatch("getSettingsLazy");
      this.$store.dispatch('getTimezones');
      // this.$store.dispatch('saveProfileLanguage', {language_code: code});
      this.$store.dispatch('getCountries');
      this.$store.dispatch('getLanguages');
      this.$store.dispatch('getSites');
      this.$store.dispatch('getCurrencies');
      this.$store.dispatch('getPaymentMethods');
      this.$store.dispatch('getWallets');
      this.$store.dispatch('getDedicatedDomainCount');
      this.$store.dispatch('getPostPlatforms');
      this.$store.dispatch('getMessengers');
    }
    const routeName = this.$route.name || '';
    document.title = `${this.$t(routeName)} - ${this.$config.pageTitle}`;
  },
  mounted() {
    if (/iPhone/i.test(navigator.userAgent)) {
      this.iphone = true;
      document.body.setAttribute('iphone', this.iphone);
    }

    const docClasses = document.body.classList;
    const isWindows = navigator.platform.startsWith('Win');

    if (isWindows) {
      initScrollbar('sidebar');
      initScrollbar('sidebar-wrapper');
      docClasses.add('perfect-scrollbar-on');
    } else {
      docClasses.add('perfect-scrollbar-off');
    }

    this.intercomUnreadCount = this.intercom.unreadCoun;

    this.$nextTick(() => {
      this.updateTrafficBlockAlertHeight();
      this.initTrafficBlockAlertObserver();
    });
  },
  unmounted() {
    this.destroyTrafficBlockAlertObserver();
  },
  methods: {
    updateTrafficBlockAlertHeight() {
      if (this.showTrafficBlockAlert && this.$refs.trafficBlockAlert) {
        // mobile empty area fix
        this.trafficBlockAlertHeight = this.$refs.trafficBlockAlert.offsetHeight - 40;
        return;
      }

      this.trafficBlockAlertHeight = 0;
    },
    initTrafficBlockAlertObserver() {
      if (!this.showTrafficBlockAlert || !this.$refs.trafficBlockAlert || !window.ResizeObserver) {
        return;
      }

      this.trafficBlockAlertResizeObserver = new ResizeObserver(() => {
        this.updateTrafficBlockAlertHeight();
      });
      this.trafficBlockAlertResizeObserver.observe(this.$refs.trafficBlockAlert);
    },
    destroyTrafficBlockAlertObserver() {
      if (!this.trafficBlockAlertResizeObserver) {
        return;
      }

      this.trafficBlockAlertResizeObserver.disconnect();
      this.trafficBlockAlertResizeObserver = null;
    },
    isExternalContactLink(type) {
      return ['email', 'ms_teams', 'contact_us_form'].includes(type);
    },
    getSocialLink(type, value) {
      switch (type) {
        case 'telegram':
          return `tg://resolve?domain=${value.replace('@', '')}`;
        case 'skype':
          return `skype:${value.replace('live:.cid.', '')}?chat`;
        case 'email':
          return `mailto:${value}`;
        case 'ms_teams':
        case 'contact_us_form':
          return value;
        default:
          return '';
      }
    },
    toggleSidebar() {
      if (this.$sidebar.showSidebar.value) {
        this.$sidebar.displaySidebar(false);
      }
    },

    formatMenuBlock(menu) {
      return menu.filter((item) => {
        if (item.title === 'sidebar.menu.marketingTools.mediaBuying') {
          return this.$store.state.profile.user.media_buying_enabled;
        }
        return true;
      });
    },

    formatMenuItems(menu) {
      return menu.filter((item) => {
        if (!item.path) {
          return true;
        }

        if (item.title === 'sidebar.menu.marketingTools.dedicatedDomains') {
          return !!this.$store.state.domains.dedicatedDomainCount.count;
        }

        if (item.name === 'routes.subAffiliates') {
          return this.$store.state.profile.user.referral_program_enabled;
        } else if (item.name === 'routes.subAffiliatesReports') {
          return this.$store.state.profile.user.referral_program_enabled;
        } else if (item.name === 'routes.mediaBuying') {
          return this.$store.state.profile.user.media_buying_enabled;
        } else if (item.name === 'routes.androidApps') {
            return this.$store.state.settings.optional_sections.mobile_apps_available;
        } else if (item.name === 'routes.cpaConversions') {
          return this.$store.state.settings.optional_sections.cpa_conversions_available;
        } else if (item.name === 'routes.kpi') {
          return this.$store.state.settings.optional_sections.kpi_bonuses_available;
        }
        else {
          return true;
        }
      });
    },

    formatPath(item) {
      const { urlQueries } = this.$store.state.misc;
      if (urlQueries[item.name]) {
        const urlQuery = lzString.compressToEncodedURIComponent(JSON.stringify(urlQueries[item.name]));

        return `${item.path}/${urlQuery}`;
      } else {
        return item.path;
      }
    },
    toggleLivechat() {
      if (!this.$store.state.settings.intercom) {
        return;
      }

      if (!this.$store.state.settings.intercom.live_chat_available) {
        this.$sidebar.displaySidebar(false);
        this.$router.push({ name: 'routes.contacts' });
        return;
      }

      if (!this.intercom.visible) {
        this.intercom.show();
        this.intercomUnreadCount = 0;
      } else {
        this.intercom.hide();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.sidebar-mini {
  .public-manager {
    display: none;
  }

  .traffic-block-alert {
    left: 60px;
  }
}

.public-manager {
  width: 100%;
  margin: 12px 16px;
  padding: 16px;
  border-radius: 8px;
  background-color: #464d4b;
  color: #fff;
  overflow: hidden;

  .title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 16px;
    font-weight: 600;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin-right: 2px;
    }

    i {
      font-size: 16px !important;
      margin-right: 0 !important;
      color: #fff !important;
    }
  }

  .note {
    font-size: 12px;
    margin-top: 8px;
    white-space: pre-wrap;
    overflow: hidden;
  }

  .label {
    height: 17px;
    font-size: 12px;
    margin-top: 2px;
  }

  .contact {
    display: flex;
    align-items: center;
    height: 19px;
    font-size: 14px;
    margin-top: 8px;

    a {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: #fff;

      &:hover {
        text-decoration: none;
      }
    }

    i, img {
      margin-right: 4px!important;
      font-size: 16px;
      min-width: 16px;
      max-width: 16px;
    }
  }
}


.main-panel {
  margin-top: var(--traffic-block-alert-offset, 0px);

  .navbar-absolute.navbar.top-navbar {
    top: var(--traffic-block-alert-offset, 0px);
  }
}

.traffic-block-alert {
  position: fixed;
  // mobile empty area fix
  top: -40px;
  padding: 46px 4px 6px 4px;

  left: 260px;
  right: 0;
  z-index: 100000030;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffeff1;
  color: #ff5c70;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.67;
  min-height: 32px;

  i {
    font-size: 14px;
  }
}

@media (max-width: 991px) {
  .traffic-block-alert {
    left: 0;
  }
}
</style>
<style lang="scss">
.fade-enter-active {
  transition: opacity 0.5s;
}

.fade-enter, .fade-leave-to, .fade-leave-active /*  in <2.1.8 */
{
  opacity: 0;
}

.tab-count {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-text-red);
  color: #fff;
  font-weight: bold;
  font-size: 10px;
  height: 16px;
  min-width: 16px;
  padding: 0 2px;
  border-radius: 50%;
}

$scaleSize: 0.95;
@keyframes zoomIn95 {
  from {
    opacity: 0;
    transform: scale3d($scaleSize, $scaleSize, $scaleSize);
  }
  to {
    opacity: 1;
  }
}

.main-panel .zoomIn {
  animation-name: zoomIn95;
}

@keyframes zoomOut95 {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
    transform: scale3d($scaleSize, $scaleSize, $scaleSize);
  }
}

.main-panel .zoomOut {
  animation-name: zoomOut95;
}
</style>
